import { NewsletterStatus } from "../config/Enums";
import { IApiResponse, INewsletter, ISubscriber, ISubscriberImport } from "../models/Interfaces";
import { BaseService } from "./BaseService";

export class SubscriberService extends BaseService {
  private splitSubscriberName = (name: string): { Firstname: string; Lastname: string } => {
    const trimmedName = (name ?? "").trim().replace(/\s+/g, " ");

    if (!trimmedName) {
      return {
        Firstname: "",
        Lastname: ""
      };
    }

    const [firstname, ...lastnameParts] = trimmedName.split(" ");

    return {
      Firstname: firstname,
      Lastname: lastnameParts.join(" ")
    };
  };

  public getSubscribers = async () : Promise<Array<ISubscriber>> => {
    if (this.siteKey) {
      const url: string = `/Subscriber/GetSubscribers/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success && res.StatusCode === 200 && res.ReturnData) {
        const subs = res.ReturnData.map((sub: ISubscriber) => ({
          ...sub,
          SiteId: this.siteKey
        }));

        return subs;
      } else if (res?.Success && res.StatusCode === 200) {
        return [];
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Failed to retrieve site subscribers");
        }
      }
    }
    return [];
  }

  public saveSubscriber = async (sub: ISubscriber, newsub: boolean) : Promise<ISubscriber | null> => {
    if (this.siteKey && sub) {
      const url: string = newsub
                          ? `/Subscriber/AddSiteSubscriber`
                          : `/Subscriber/UpdateSitePreferences`;
      const { Firstname, Lastname } = this.splitSubscriberName(sub.Name);
      const model = {
        SiteId: this.siteKey,
        UserId: sub.UserId,
        EmailAddress: sub.EmailAddress,
        Firstname,
        Lastname,
        Enabled: sub.Enabled,
        Newsletter: sub.Newsletter
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);

      if (res?.Success && res.StatusCode === 200 && res.ReturnData) {
        const sub = res.ReturnData;
        sub.SiteId = this.siteKey;
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save subscriber details");
      }
    }
    return null;
  };

  public deleteSubscriber = async (sub: ISubscriber) : Promise<boolean> => {
    if (this.siteKey && sub) {
      const url: string = `/Subscriber/RemoveFromSite`;
      const model = {
        UserId: sub.UserId,
        SiteId: this.siteKey
      };
      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);
      if (res?.Success && res.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove subscriber details");
      }
    }
    return false;
  }

  public uploadValidSubscribers = async (subs: Array<ISubscriberImport>) : Promise<boolean> => {
    if (this.siteKey && subs.length > 0) {
      const url: string = `/Subscriber/Import`;
      const model = {
        SiteId: this.siteKey,
        Subscribers: subs
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);

      if (res?.Success && res.StatusCode === 200) {
        this.EmitDataSuccess(res.Messages[0]);
        return true;
      } else {
        this.EmitDataError(res, "Failed to import subscriber details");
      }
    }
    return false;
  }

  public getNewsletters = async () : Promise<Array<INewsletter>> => {
    if (this.siteKey) {
      const url: string = `/Subscriber/GetNewsletters/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success && res.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Failed to retrieve newsletters");
        }
      }
    }
    return [];
  }

  public deleteNewsletter = async (id: number) : Promise<boolean> => {
    if (this.siteKey && id) {
      const url: string = `/Subscriber/RemoveNewsletter`;
      const model = {
        NewsletterId: id,
        SiteId: this.siteKey
      };
      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);

      if (res?.Success && res.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove newsletter");
      }
    }
    return false;
  }

  public saveNewsletter = async (newsletter: INewsletter) : Promise<INewsletter | null> => {
    if (this.siteKey && newsletter) {
      const url: string = `/Subscriber/SaveNewsletter`;
      const res = await this._ajaxService.Post<IApiResponse>(url, newsletter, true);
      if (res?.Success && res.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save newsletter");
      }
    }
    return null;
  }

  public sendNewsletter = async (newsletter: INewsletter) : Promise<boolean> => {
    if (this.siteKey && newsletter) {
      const url: string = `/Subscriber/SendNewsletter`;
      const model = {
        SiteId: this.siteKey,
        NewsLetterId: newsletter.Id
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);
      if (res?.Success && res.StatusCode === 200 && res.ReturnData) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to send newsletter");
      }
    }
    return false;
  }

  public newSubscriber = () : ISubscriber | null => {
    if (this.siteKey) {
      return {
        Id: 0,
        DateAdded: "",
        EmailAddress: "",
        Enabled: true,
        Newsletter: true,
        Name: "",
        UserId: "",
        SiteId: this.siteKey
      };
    }
    return null;
  }

  public newNewsletter = () : INewsletter | null => {
    if (this.siteKey) {
      return {
        SiteId: this.siteKey,
        BodyHtml: "",
        Name: "",
        Subject: "",
        Status: NewsletterStatus.Unsent,
        DateAdded: new Date().toISOString(),
        DateModified: new Date().toISOString(),
        Id: 0,
        StatusDescription: "unsent"
      }
    }
    return null;
  }
}
